/**
 * Référentiel OHADA — Système Normal.
 *
 * ⚠️ Codes REF, libellés et formules EXTRAITS TELS QUELS de la console HTML,
 * pas retapés. Recopier à la main cent cinquante lignes de codes comptables,
 * c'est garantir au moins une faute — et une faute ici ne se voit pas : elle
 * produit un bilan qui a l'air normal et qui est faux d'un montant qu'on ne
 * repère qu'au contrôle d'équilibre, voire jamais.
 *
 * Seul le MAPPING préfixes⇆REF est configurable. La structure et les libellés
 * sont le référentiel officiel : ils ne se modifient pas.
 */

/** Un poste du bilan ou du compte de résultat. */
export interface PosteOhada {
  ref: string
  label: string
  /** Côté à retenir dans le grand-livre : débiteur (actif/charges) ou créditeur. */
  side?: 'D' | 'C'
  /** Formule : somme signée d'autres REF. Un poste à formule n'agrège aucun compte. */
  f?: Array<['+' | '-', string]>
  head?: boolean
  grand?: boolean
  total?: boolean
  strong?: boolean
  net?: boolean
  bal?: boolean
  sign?: '+' | '-'
  /** Saisi à la main, jamais déduit des comptes (écarts de conversion). */
  manual?: boolean
  /** `res` = le résultat net, injecté depuis le compte de résultat. */
  special?: string
  /** Poste présenté en négatif dans l'état (charge dans une cascade de produits). */
  neg?: boolean
}

export const PREFIXES_PAR_DEFAUT: Record<string, string[]> = {
  // ── Bilan Actif ──
  AE:['211'],AF:['212','213','214'],AG:['215','216'],AH:['218','219'],
  AJ:['22'],AK:['231'],AL:['232','233','234','235','237','238'],AM:['241','242','243','244','246','247','248'],AN:['245'],AP:['25'],
  AR:['26'],AS:['27'],
  BA:['485','488'],BB:['3'],
  BH:['409'],BI:['41'],BJ:['42','43','44','45','46','47'],
  BQ:['50'],BR:['51'],BS:['52','53','54','55','57','58'],BU:[],
  // ── Bilan Passif ──
  CA:['101','102','103'],CB:['109'],CD:['104'],CE:['105','106'],CF:['111','112','113'],CG:['118'],CH:['12'],CL:['14'],CM:['15'],
  DA:['16'],DB:['17'],DC:['19'],
  DH:['481','482','484'],DI:['419'],DJ:['40'],DK:['42','43','44'],DM:['45','46','47'],DN:['499'],
  DQ:['564','565'],DR:['561','566'],DV:[],
  // ── Compte de résultat ──
  TA:['701'],RA:['601'],RB:['6031'],
  TB:['702','703','704'],TC:['705','706'],TD:['707','708'],
  TE:['73'],TF:['72'],TG:['71'],TH:['75'],TI:['781'],
  RC:['602'],RD:['6032'],RE:['604','605','608'],RF:['6033'],RG:['61'],RH:['62','63'],RI:['64'],RJ:['65'],
  RK:['66'],
  TJ:['791','798','799'],RL:['681','691'],
  TK:['77'],TL:['797'],TM:['787'],RM:['67'],RN:['697'],
  TN:['82'],TO:['84','86','88'],RO:['81'],RP:['83','85'],RQ:['87'],RS:['89'],
};
// ── Bilan Actif (REF officiels + formules) ──

export const BILAN_ACTIF: PosteOhada[] = [
  {ref:'AD',label:'IMMOBILISATIONS INCORPORELLES',head:true,f:[['+','AE'],['+','AF'],['+','AG'],['+','AH']]},
  {ref:'AE',label:'Frais de développement et de prospection',side:'D'},
  {ref:'AF',label:'Brevets, licences, logiciels et droits similaires',side:'D'},
  {ref:'AG',label:'Fonds commercial et droit au bail',side:'D'},
  {ref:'AH',label:'Autres immobilisations incorporelles',side:'D'},
  {ref:'AI',label:'IMMOBILISATIONS CORPORELLES',head:true,f:[['+','AJ'],['+','AK'],['+','AL'],['+','AM'],['+','AN'],['+','AP']]},
  {ref:'AJ',label:'Terrains',side:'D'},
  {ref:'AK',label:'Bâtiments',side:'D'},
  {ref:'AL',label:'Aménagements, agencements et installations',side:'D'},
  {ref:'AM',label:'Matériel, mobilier et actifs biologiques',side:'D'},
  {ref:'AN',label:'Matériel de transport',side:'D'},
  {ref:'AP',label:'Avances et acomptes versés sur immobilisations',side:'D'},
  {ref:'AQ',label:'IMMOBILISATIONS FINANCIÈRES',head:true,f:[['+','AR'],['+','AS']]},
  {ref:'AR',label:'Titres de participation',side:'D'},
  {ref:'AS',label:'Autres immobilisations financières',side:'D'},
  {ref:'AZ',label:'TOTAL ACTIF IMMOBILISÉ',grand:true,f:[['+','AD'],['+','AI'],['+','AQ']]},
  {ref:'BA',label:'ACTIF CIRCULANT HAO',head:true,side:'D'},
  {ref:'BB',label:'STOCKS ET EN-COURS',head:true,side:'D'},
  {ref:'BG',label:'CRÉANCES ET EMPLOIS ASSIMILÉS',head:true,f:[['+','BH'],['+','BI'],['+','BJ']]},
  {ref:'BH',label:'Fournisseurs, avances versées',side:'D'},
  {ref:'BI',label:'Clients',side:'D'},
  {ref:'BJ',label:'Autres créances',side:'D'},
  {ref:'BK',label:'TOTAL ACTIF CIRCULANT',grand:true,f:[['+','BA'],['+','BB'],['+','BG']]},
  {ref:'BQ',label:'Titres de placement',side:'D'},
  {ref:'BR',label:'Valeurs à encaisser',side:'D'},
  {ref:'BS',label:'Banques, chèques postaux, caisse et assimilés',side:'D'},
  {ref:'BT',label:'TOTAL TRÉSORERIE-ACTIF',grand:true,f:[['+','BQ'],['+','BR'],['+','BS']]},
  {ref:'BU',label:'Écart de conversion-Actif',side:'D',manual:true},
  {ref:'BZ',label:'TOTAL GÉNÉRAL',grand:true,total:true,f:[['+','AZ'],['+','BK'],['+','BT'],['+','BU']]},
];

export const BILAN_PASSIF: PosteOhada[] = [
  {ref:'CA',label:'Capital',side:'C'},
  {ref:'CB',label:'Apporteurs, capital non appelé (-)',side:'D',neg:true},
  {ref:'CD',label:'Primes liées au capital social',side:'C'},
  {ref:'CE',label:'Écarts de réévaluation',side:'C'},
  {ref:'CF',label:'Réserves indisponibles',side:'C'},
  {ref:'CG',label:'Réserves libres',side:'C'},
  {ref:'CH',label:'Report à nouveau (+ ou -)',side:'C'},
  {ref:'CJ',label:"Résultat net de l'exercice (bénéfice + / perte -)",special:'res'},
  {ref:'CL',label:"Subventions d'investissement",side:'C'},
  {ref:'CM',label:'Provisions réglementées',side:'C'},
  {ref:'CP',label:'TOTAL CAPITAUX PROPRES ET RESSOURCES ASSIMILÉES',grand:true,f:[['+','CA'],['-','CB'],['+','CD'],['+','CE'],['+','CF'],['+','CG'],['+','CH'],['+','CJ'],['+','CL'],['+','CM']]},
  {ref:'DA',label:'Emprunts et dettes financières diverses',side:'C'},
  {ref:'DB',label:'Dettes de location-acquisition',side:'C'},
  {ref:'DC',label:'Provisions pour risques et charges',side:'C'},
  {ref:'DD',label:'TOTAL DETTES FINANCIÈRES ET RESSOURCES ASSIMILÉES',grand:true,f:[['+','DA'],['+','DB'],['+','DC']]},
  {ref:'DF',label:'TOTAL RESSOURCES STABLES',grand:true,f:[['+','CP'],['+','DD']]},
  {ref:'DH',label:'Dettes circulantes HAO',side:'C'},
  {ref:'DI',label:'Clients, avances reçues',side:'C'},
  {ref:'DJ',label:"Fournisseurs d'exploitation",side:'C'},
  {ref:'DK',label:'Dettes fiscales et sociales',side:'C'},
  {ref:'DM',label:'Autres dettes',side:'C'},
  {ref:'DN',label:'Provisions pour risques à court terme',side:'C'},
  {ref:'DP',label:'TOTAL PASSIF CIRCULANT',grand:true,f:[['+','DH'],['+','DI'],['+','DJ'],['+','DK'],['+','DM'],['+','DN']]},
  {ref:'DQ',label:"Banques, crédits d'escompte",side:'C'},
  {ref:'DR',label:'Banques, établissements financiers et crédits de trésorerie',side:'C'},
  {ref:'DT',label:'TOTAL TRÉSORERIE-PASSIF',grand:true,f:[['+','DQ'],['+','DR']]},
  {ref:'DV',label:'Écart de conversion-Passif',side:'C',manual:true},
  {ref:'DZ',label:'TOTAL GÉNÉRAL',grand:true,total:true,f:[['+','DF'],['+','DP'],['+','DT'],['+','DV']]},
];

export const RESULTAT: PosteOhada[] = [
  {ref:'TA',label:'Ventes de marchandises',sign:'+',side:'C'},
  {ref:'RA',label:'Achats de marchandises',sign:'-',side:'D'},
  {ref:'RB',label:'Variation de stocks de marchandises',sign:'-',side:'D'},
  {ref:'XA',label:'MARGE COMMERCIALE',bal:true,f:[['+','TA'],['-','RA'],['-','RB']]},
  {ref:'TB',label:'Ventes de produits fabriqués',sign:'+',side:'C'},
  {ref:'TC',label:'Travaux, services vendus',sign:'+',side:'C'},
  {ref:'TD',label:'Produits accessoires',sign:'+',side:'C'},
  {ref:'XB',label:"CHIFFRE D'AFFAIRES",bal:true,strong:true,f:[['+','TA'],['+','TB'],['+','TC'],['+','TD']]},
  {ref:'TE',label:'Production stockée (ou déstockage)',sign:'+',side:'C'},
  {ref:'TF',label:'Production immobilisée',sign:'+',side:'C'},
  {ref:'TG',label:"Subventions d'exploitation",sign:'+',side:'C'},
  {ref:'TH',label:'Autres produits',sign:'+',side:'C'},
  {ref:'TI',label:"Transferts de charges d'exploitation",sign:'+',side:'C'},
  {ref:'RC',label:'Achats de matières premières et fournitures liées',sign:'-',side:'D'},
  {ref:'RD',label:'Variation de stocks de matières premières et fournitures',sign:'-',side:'D'},
  {ref:'RE',label:'Autres achats',sign:'-',side:'D'},
  {ref:'RF',label:"Variation de stocks d'autres approvisionnements",sign:'-',side:'D'},
  {ref:'RG',label:'Transports',sign:'-',side:'D'},
  {ref:'RH',label:'Services extérieurs',sign:'-',side:'D'},
  {ref:'RI',label:'Impôts et taxes',sign:'-',side:'D'},
  {ref:'RJ',label:'Autres charges',sign:'-',side:'D'},
  {ref:'XC',label:'VALEUR AJOUTÉE',bal:true,f:[['+','XB'],['+','TE'],['+','TF'],['+','TG'],['+','TH'],['+','TI'],['-','RA'],['-','RB'],['-','RC'],['-','RD'],['-','RE'],['-','RF'],['-','RG'],['-','RH'],['-','RI'],['-','RJ']]},
  {ref:'RK',label:'Charges de personnel',sign:'-',side:'D'},
  {ref:'XD',label:"EXCÉDENT BRUT D'EXPLOITATION",bal:true,f:[['+','XC'],['-','RK']]},
  {ref:'TJ',label:"Reprises d'amortissements, provisions et dépréciations",sign:'+',side:'C'},
  {ref:'RL',label:'Dotations aux amortissements, provisions et dépréciations',sign:'-',side:'D'},
  {ref:'XE',label:"RÉSULTAT D'EXPLOITATION",bal:true,strong:true,f:[['+','XD'],['+','TJ'],['-','RL']]},
  {ref:'TK',label:'Revenus financiers et assimilés',sign:'+',side:'C'},
  {ref:'TL',label:'Reprises de provisions et dépréciations financières',sign:'+',side:'C'},
  {ref:'TM',label:'Transferts de charges financières',sign:'+',side:'C'},
  {ref:'RM',label:'Frais financiers et charges assimilées',sign:'-',side:'D'},
  {ref:'RN',label:'Dotations aux provisions et dépréciations financières',sign:'-',side:'D'},
  {ref:'XF',label:'RÉSULTAT FINANCIER',bal:true,f:[['+','TK'],['+','TL'],['+','TM'],['-','RM'],['-','RN']]},
  {ref:'XG',label:'RÉSULTAT DES ACTIVITÉS ORDINAIRES',bal:true,strong:true,f:[['+','XE'],['+','XF']]},
  {ref:'TN',label:"Produits des cessions d'immobilisations",sign:'+',side:'C'},
  {ref:'TO',label:'Autres produits HAO',sign:'+',side:'C'},
  {ref:'RO',label:'Valeurs comptables des cessions d\'immobilisations',sign:'-',side:'D'},
  {ref:'RP',label:'Autres charges HAO',sign:'-',side:'D'},
  {ref:'XH',label:'RÉSULTAT HORS ACTIVITÉS ORDINAIRES',bal:true,f:[['+','TN'],['+','TO'],['-','RO'],['-','RP']]},
  {ref:'RQ',label:'Participation des travailleurs',sign:'-',side:'D'},
  {ref:'RS',label:'Impôts sur le résultat',sign:'-',side:'D'},
  {ref:'XI',label:"RÉSULTAT NET",bal:true,strong:true,net:true,f:[['+','XG'],['+','XH'],['-','RQ'],['-','RS']]},
];
